import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import { useAtlasRecommendations, useRoomCapacities } from "@/services/recommendation";
import type {
  ManuallyDateRangeResponse,
  RecommendationRequest,
  RoomCapacityResponse,
} from "@/types/recommendation";

const atlasSchema = z
  .object({
    startDate: z.string().min(1, "Start date is required."),
    endDate: z.string().min(1, "End date is required."),
    numberOfNights: z.coerce.number().int().min(1, "Number of nights must be greater than 0."),
    estimatedAttendance: z.coerce.number().int().min(1, "Estimated attendance must be greater than 0."),
    includeWeekends: z.boolean(),
    setPreferredDateRange: z.boolean(),
    preferredDateRange: z.object({
      preferredStartDate: z.string().optional(),
      preferredEndDate: z.string().optional(),
    }),
    isModifyNightGuest: z.boolean(),
    daywiseNightGuests: z.array(
      z.object({
        nightLabel: z.string(),
        nightGuest: z.coerce.number().int().min(1, "Night guest count must be greater than 0."),
      })
    ),
  })
  .superRefine((data, ctx) => {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end < start) {
      ctx.addIssue({ code: "custom", path: ["endDate"], message: "End date should be greater than start date." });
    }

    if (data.setPreferredDateRange) {
      const preferredStart = data.preferredDateRange.preferredStartDate;
      const preferredEnd = data.preferredDateRange.preferredEndDate;

      if (!preferredStart) {
        ctx.addIssue({ code: "custom", path: ["preferredDateRange", "preferredStartDate"], message: "Select From date" });
      }
      if (!preferredEnd) {
        ctx.addIssue({ code: "custom", path: ["preferredDateRange", "preferredEndDate"], message: "Select To date" });
      }

      if (preferredStart && preferredEnd) {
        const pStart = new Date(preferredStart);
        const pEnd = new Date(preferredEnd);

        if (pEnd < pStart) {
          ctx.addIssue({
            code: "custom",
            path: ["preferredDateRange", "preferredEndDate"],
            message: "To date should be greater than From date",
          });
        }

        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          if (pStart < start || pEnd > end) {
            ctx.addIssue({
              code: "custom",
              path: ["preferredDateRange", "preferredEndDate"],
              message: `Preferred date range should be within ${data.startDate} and ${data.endDate}`,
            });
          }
        }

        const days = Math.floor((pEnd.getTime() - pStart.getTime()) / 86_400_000) + 1;
        if (days > data.numberOfNights) {
          ctx.addIssue({
            code: "custom",
            path: ["preferredDateRange", "preferredEndDate"],
            message: `Preferred date range should not exceed number of nights: ${data.numberOfNights}`,
          });
        }
      }
    }
  });

type AtlasFormValues = z.infer<typeof atlasSchema>;

interface AtlasRecommendationFormProps {
  isNlqChatEnabled?: boolean;
  onSwitchToNlq?: () => void;
  onResult: (result: ManuallyDateRangeResponse) => void;
  onPreparePrompt?: (prompt: string) => void;
}

function formatDisplayDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function buildNlqPrompt(values: AtlasFormValues): string {
  const parts: string[] = [];

  if (values.startDate && values.endDate) {
    parts.push(`from ${formatDisplayDate(values.startDate)} to ${formatDisplayDate(values.endDate)}`);
  } else if (values.startDate) {
    parts.push(`starting ${formatDisplayDate(values.startDate)}`);
  } else if (values.endDate) {
    parts.push(`ending ${formatDisplayDate(values.endDate)}`);
  }

  if (values.numberOfNights > 0) {
    parts.push(`for ${values.numberOfNights} night${values.numberOfNights !== 1 ? "s" : ""}`);
  }

  if (values.estimatedAttendance > 0) {
    parts.push(`with ${values.estimatedAttendance} estimated attendee${values.estimatedAttendance !== 1 ? "s" : ""}`);
  }

  parts.push(values.includeWeekends ? "including weekends" : "excluding weekends");

  if (
    values.setPreferredDateRange &&
    values.preferredDateRange.preferredStartDate &&
    values.preferredDateRange.preferredEndDate
  ) {
    parts.push(
      `with preferred dates from ${formatDisplayDate(values.preferredDateRange.preferredStartDate)} to ${formatDisplayDate(values.preferredDateRange.preferredEndDate)}`
    );
  }

  if (values.isModifyNightGuest && values.daywiseNightGuests.length > 0) {
    const nightDetails = values.daywiseNightGuests
      .map((n) => `${n.nightLabel}: ${n.nightGuest} guest${n.nightGuest !== 1 ? "s" : ""}`)
      .join(", ");
    parts.push(`with per-night guests (${nightDetails})`);
  }

  return `Find availability ${parts.join(", ")}.`;
}

function getMaxCapacity(endDate: string, capacities: RoomCapacityResponse[] | undefined): number {
  if (!capacities || capacities.length === 0 || !endDate) return Number.MAX_SAFE_INTEGER;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return Number.MAX_SAFE_INTEGER;

  const applicable = capacities
    .filter((x) => new Date(x.effectiveFrom) <= end)
    .sort((a, b) => +new Date(b.effectiveFrom) - +new Date(a.effectiveFrom))[0];

  return applicable?.capacity ?? Number.MAX_SAFE_INTEGER;
}

function formatDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function AtlasRecommendationForm({
  isNlqChatEnabled = true,
  onSwitchToNlq,
  onResult,
  onPreparePrompt,
}: AtlasRecommendationFormProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  const atlasMutation = useAtlasRecommendations();
  const { data: roomCapacities } = useRoomCapacities();

  const form = useForm<AtlasFormValues>({
    resolver: zodResolver(atlasSchema),
    defaultValues: {
      startDate: "",
      endDate: "",
      numberOfNights: 0,
      estimatedAttendance: 0,
      includeWeekends: false,
      setPreferredDateRange: false,
      preferredDateRange: {
        preferredStartDate: "",
        preferredEndDate: "",
      },
      isModifyNightGuest: false,
      daywiseNightGuests: [],
    },
    mode: "onSubmit",
  });

  const nightsField = useFieldArray({
    control: form.control,
    name: "daywiseNightGuests",
  });

  const startDate = form.watch("startDate");
  const endDate = form.watch("endDate");
  const numberOfNights = form.watch("numberOfNights");
  const isModifyNightGuest = form.watch("isModifyNightGuest");
  const setPreferredDateRange = form.watch("setPreferredDateRange");
  const preferredStartDate = form.watch("preferredDateRange.preferredStartDate");
  const preferredEndDate = form.watch("preferredDateRange.preferredEndDate");

  const maxCapacity = useMemo(
    () => getMaxCapacity(endDate, roomCapacities),
    [endDate, roomCapacities]
  );

  useEffect(() => {
    if (!preferredStartDate || numberOfNights <= 0 || preferredEndDate) return;

    const start = new Date(preferredStartDate);
    if (Number.isNaN(start.getTime())) return;

    const calculated = new Date(start);
    calculated.setDate(calculated.getDate() + numberOfNights - 1);

    if (endDate) {
      const overallEnd = new Date(endDate);
      if (!Number.isNaN(overallEnd.getTime()) && calculated > overallEnd) {
        form.setValue("preferredDateRange.preferredEndDate", formatDateInput(overallEnd));
        return;
      }
    }

    form.setValue("preferredDateRange.preferredEndDate", formatDateInput(calculated));
  }, [preferredStartDate, preferredEndDate, numberOfNights, endDate, form]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const overallStart = new Date(startDate);
    const overallEnd = new Date(endDate);
    if (Number.isNaN(overallStart.getTime()) || Number.isNaN(overallEnd.getTime())) return;

    if (preferredStartDate) {
      const prefStart = new Date(preferredStartDate);
      if (!Number.isNaN(prefStart.getTime()) && (prefStart < overallStart || prefStart > overallEnd)) {
        form.setValue("preferredDateRange.preferredStartDate", "");
      }
    }

    if (preferredEndDate) {
      const prefEnd = new Date(preferredEndDate);
      if (!Number.isNaN(prefEnd.getTime()) && (prefEnd < overallStart || prefEnd > overallEnd)) {
        form.setValue("preferredDateRange.preferredEndDate", "");
      }
    }
  }, [startDate, endDate, preferredStartDate, preferredEndDate, form]);

  function resetForm() {
    form.reset({
      startDate: "",
      endDate: "",
      numberOfNights: 0,
      estimatedAttendance: 0,
      includeWeekends: false,
      setPreferredDateRange: false,
      preferredDateRange: {
        preferredStartDate: "",
        preferredEndDate: "",
      },
      isModifyNightGuest: false,
      daywiseNightGuests: [],
    });
    nightsField.replace([]);
  }

  function regenerateNightGuests(nextNights: number) {
    if (nextNights <= 0) {
      nightsField.replace([]);
      return;
    }

    const generated = Array.from({ length: nextNights }, (_, idx) => ({
      nightLabel: `Night ${idx + 1}`,
      nightGuest: 1,
    }));
    nightsField.replace(generated);
  }

  async function onToggleModifyNightGuests(checked: boolean) {
    if (!checked) {
      form.setValue("isModifyNightGuest", false);
      nightsField.replace([]);
      return;
    }

    const errors: string[] = [];
    if (!startDate || !endDate) {
      errors.push("Please select valid start and end date.");
    }
    if (!numberOfNights || numberOfNights <= 0) {
      errors.push("Please enter a valid number of nights greater than 0.");
    }

    if (errors.length > 0) {
      toast.error(errors.join(" "));
      form.setValue("isModifyNightGuest", false);
      return;
    }

    form.setValue("isModifyNightGuest", true);
    regenerateNightGuests(numberOfNights);
  }

  function validateNightGuest(index: number, value: number): boolean {
    if (!roomCapacities || roomCapacities.length === 0) {
      form.setError(`daywiseNightGuests.${index}.nightGuest`, {
        type: "manual",
        message: "Unable to validate night guests because room capacities are unavailable.",
      });
      return false;
    }

    if (value < 1) {
      form.setError(`daywiseNightGuests.${index}.nightGuest`, {
        type: "manual",
        message: "The number of night guests per night must be greater than 0.",
      });
      return false;
    }

    if (value > maxCapacity) {
      form.setError(`daywiseNightGuests.${index}.nightGuest`, {
        type: "manual",
        message: `The total number of night guests per night must be ${maxCapacity} or fewer`,
      });
      return false;
    }

    form.clearErrors(`daywiseNightGuests.${index}.nightGuest`);
    return true;
  }

  async function onSubmit(values: AtlasFormValues) {
    if (values.isModifyNightGuest) {
      const allValid = values.daywiseNightGuests.every((g, idx) => validateNightGuest(idx, g.nightGuest));
      if (!allValid) return;
    }

    const payload: RecommendationRequest = {
      startDate: values.startDate,
      endDate: values.endDate,
      numberOfNights: values.numberOfNights,
      estimatedAttendance: values.estimatedAttendance,
      includeWeekends: values.includeWeekends,
      setPreferredDateRange: values.setPreferredDateRange,
      preferredDateRange: {
        preferredStartDate: values.preferredDateRange.preferredStartDate || null,
        preferredEndDate: values.preferredDateRange.preferredEndDate || null,
      },
      isModifyNightGuest: values.isModifyNightGuest,
      daywiseNightGuests: values.daywiseNightGuests,
    };

    try {
      const recommendations = await atlasMutation.mutateAsync(payload);
      const hasPreferredDate = Boolean(payload.preferredDateRange.preferredStartDate);
      const displaySummary =
        recommendations.length > 0 &&
        recommendations.every((r) =>
          r.recommendationsByDateRangeResponses.every((x) => !x.isPreferredDate)
        );

      onResult({
        recommendationsDateRanges: recommendations,
        displaySummary,
        hasPreferredDate,
      });

      setIsExpanded(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to get recommendations.";
      toast.error(message);
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
      <div className="border border-[#D0D0CE] p-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-black">General Information</label>
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="rounded-none p-1 text-slate-700 hover:bg-slate-100"
            aria-label="Toggle general information"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>

        {isExpanded && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="atlas-start-date" className="mb-1 block text-xs text-slate-700">Start Date *</label>
                <input
                  id="atlas-start-date"
                  type="date"
                  {...form.register("startDate")}
                  className="h-10 w-full rounded-none border border-slate-300 px-2 text-sm"
                />
                {form.formState.errors.startDate && (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.startDate.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="atlas-end-date" className="mb-1 block text-xs text-slate-700">End Date *</label>
                <input
                  id="atlas-end-date"
                  type="date"
                  {...form.register("endDate")}
                  className="h-10 w-full rounded-none border border-slate-300 px-2 text-sm"
                />
                {form.formState.errors.endDate && (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.endDate.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="atlas-number-of-nights" className="mb-1 block text-xs text-slate-700">Number of Nights *</label>
                <input
                  id="atlas-number-of-nights"
                  type="number"
                  min={1}
                  {...form.register("numberOfNights", {
                    valueAsNumber: true,
                    onChange: (e) => {
                      const next = Number(e.target.value || 0);
                      if (form.getValues("isModifyNightGuest")) regenerateNightGuests(next);
                    },
                  })}
                  className="h-10 w-full rounded-none border border-slate-300 px-2 text-sm"
                />
                {form.formState.errors.numberOfNights && (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.numberOfNights.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="atlas-estimated-attendance" className="mb-1 block text-xs text-slate-700">Estimated Attendance *</label>
                <input
                  id="atlas-estimated-attendance"
                  type="number"
                  min={1}
                  {...form.register("estimatedAttendance", { valueAsNumber: true })}
                  className="h-10 w-full rounded-none border border-slate-300 px-2 text-sm"
                />
                {form.formState.errors.estimatedAttendance && (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.estimatedAttendance.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsAdvancedExpanded((v) => !v)}
                className="text-sm text-slate-700 hover:underline"
              >
                Advanced Options
              </button>
              <button
                type="button"
                onClick={() => setIsAdvancedExpanded((v) => !v)}
                className="rounded-none p-1 text-slate-700 hover:bg-slate-100"
                aria-label="Toggle advanced options"
              >
                {isAdvancedExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {isAdvancedExpanded && (
              <div className="space-y-3 border-t border-[#BBBCBC] pt-3">
                <label htmlFor="atlas-preferred-date-toggle" className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    id="atlas-preferred-date-toggle"
                    type="checkbox"
                    checked={setPreferredDateRange}
                    onChange={(e) => form.setValue("setPreferredDateRange", e.target.checked)}
                    className="h-4 w-4 rounded-none border-slate-300"
                  />
                  Set preferred date range
                </label>

                {setPreferredDateRange && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="atlas-preferred-start-date" className="mb-1 block text-xs text-slate-700">From Date *</label>
                      <input
                        id="atlas-preferred-start-date"
                        type="date"
                        {...form.register("preferredDateRange.preferredStartDate")}
                        className="h-10 w-full rounded-none border border-slate-300 px-2 text-sm"
                      />
                      {form.formState.errors.preferredDateRange?.preferredStartDate && (
                        <p className="mt-1 text-xs text-red-600">
                          {form.formState.errors.preferredDateRange.preferredStartDate.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="atlas-preferred-end-date" className="mb-1 block text-xs text-slate-700">To Date *</label>
                      <input
                        id="atlas-preferred-end-date"
                        type="date"
                        {...form.register("preferredDateRange.preferredEndDate")}
                        className="h-10 w-full rounded-none border border-slate-300 px-2 text-sm"
                      />
                      {form.formState.errors.preferredDateRange?.preferredEndDate && (
                        <p className="mt-1 text-xs text-red-600">
                          {form.formState.errors.preferredDateRange.preferredEndDate.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="border-t border-[#BBBCBC] pt-3">
                  <label htmlFor="atlas-modify-night-guests-toggle" className="flex items-center gap-2 text-sm text-slate-800">
                    <input
                      id="atlas-modify-night-guests-toggle"
                      type="checkbox"
                      checked={isModifyNightGuest}
                      onChange={(e) => void onToggleModifyNightGuests(e.target.checked)}
                      className="h-4 w-4 rounded-none border-slate-300"
                    />
                    Modify night guests
                  </label>
                </div>

                {isModifyNightGuest && nightsField.fields.length > 0 && (
                  <div className="space-y-2">
                    {nightsField.fields.map((field, idx) => (
                      <div key={field.id}>
                        <div className="grid grid-cols-[1fr_110px] items-center gap-2">
                          <label htmlFor={`atlas-night-guest-${idx}`} className="text-sm text-slate-800">{field.nightLabel}</label>
                          <input
                            id={`atlas-night-guest-${idx}`}
                            type="number"
                            min={1}
                            {...form.register(`daywiseNightGuests.${idx}.nightGuest`, {
                              valueAsNumber: true,
                              onBlur: (e) => validateNightGuest(idx, Number(e.target.value || 0)),
                            })}
                            className="h-9 w-full rounded-none border border-slate-300 px-2 text-sm"
                          />
                        </div>
                        {form.formState.errors.daywiseNightGuests?.[idx]?.nightGuest && (
                          <p className="mt-1 text-xs text-red-600">
                            {form.formState.errors.daywiseNightGuests[idx]?.nightGuest?.message}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t border-[#BBBCBC] pt-3">
                  <label htmlFor="atlas-include-weekends" className="flex items-center gap-2 text-sm text-slate-800">
                    <input
                      id="atlas-include-weekends"
                      type="checkbox"
                      {...form.register("includeWeekends")}
                      className="h-4 w-4 rounded-none border-slate-300"
                    />
                    Include weekends
                  </label>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              {isNlqChatEnabled ? (
                <button
                  type="button"
                  onClick={onSwitchToNlq}
                  className="text-sm text-[#007CB0] hover:underline"
                >
                  Back to Chat
                </button>
              ) : (
                <span />
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-sm text-[#007CB0] hover:underline"
                >
                  Clear Form
                </button>
                {onPreparePrompt && (
                  <button
                    type="button"
                    onClick={() => onPreparePrompt(buildNlqPrompt(form.getValues()))}
                    className="rounded-none border border-[#007CB0] px-3 py-2 text-sm text-[#007CB0] hover:bg-blue-50 transition-colors"
                    title="Convert current selections into an NLQ prompt"
                  >
                    Ask as NLQ
                  </button>
                )}
                <button
                  type="submit"
                  disabled={atlasMutation.isPending}
                  className="rounded-none border border-black bg-black px-4 py-2 text-sm text-white disabled:opacity-50"
                >
                  {atlasMutation.isPending ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
